import {ConnectedField} from 'quickstart/components/controls/ConnectedField'
import {IconButton} from 'quickstart/components/controls/IconButton'
import {FlexGrid} from 'quickstart/components/layout/FlexGrid'
import {SearchTermsInput} from 'quickstart/components/tizra/SearchTermsInput'
import {useSearchConfig} from 'quickstart/hooks'
import {forwardRef, useId} from 'react'
import * as Final from 'react-final-form'
import {logger, partitionProps} from 'tizra'

const log = logger('quickstart/components/tizra/QuickSearch')

export const useQuickSearch = ({params = {}, relative = false}) => {
  const formId = useId()
  const searchConfig = useSearchConfig({mode: 'quick'})
  return {
    formId,
    params,
    relative,
    searchConfig,
  }
}

const partitionHookProps = partitionProps([
  'formId',
  'params',
  'relative',
  'searchConfig',
])

export const QuickSearchForm = forwardRef((mixedProps, ref) => {
  const [{formId, params, relative}, {children, ...props}] =
    partitionHookProps(mixedProps)

  const onSubmit = ({terms}) => {
    const usp = new URLSearchParams()
    if (terms) {
      usp.set('searchParam-terms', terms)
    }
    Object.entries(params).forEach(([k, v]) => {
      if (k === 'terms' && terms) {
        v += terms
      }
      usp.set(`searchParam-${k}`, v)
    })
    const queryString = usp.toString()
    window.location.href =
      (relative ? '' : '/') +
      '~searchResults' +
      (queryString ? '?' : '') +
      queryString
  }

  const render =
    typeof children === 'function'
      ? children
      : renderProps => (
          <form {...renderProps} {...props}>
            {children}
          </form>
        )

  return (
    <Final.Form
      onSubmit={onSubmit}
      render={({handleSubmit}) =>
        render({id: formId, onSubmit: handleSubmit, ref})
      }
    />
  )
})

export const QuickSearchInput = forwardRef((mixedProps, ref) => {
  const [{formId, searchConfig}, {children, ...props}] =
    partitionHookProps(mixedProps)

  const render =
    children &&
    log.assert(
      typeof children === 'function',
      'QuickSearchInput children can only be a render function',
    )
      ? children
      : renderProps => (
          <ConnectedField
            component={SearchTermsInput}
            {...renderProps}
            {...props}
          />
        )

  return render({form: formId, name: 'terms', search: searchConfig, ref})
})

export const QuickSearchButton = forwardRef((mixedProps, ref) => {
  const [{formId}, {children, ...props}] = partitionHookProps(mixedProps)

  const render =
    typeof children === 'function'
      ? children
      : renderProps => <IconButton icon="search" {...renderProps} {...props} />

  return render({form: formId, type: 'submit', ref})
})

const _QuickSearch = forwardRef((mixedProps, ref) => {
  const [hookProps, {button = true, placeholder, children, ...props}] =
    partitionHookProps(mixedProps)

  const state = useQuickSearch(hookProps)

  const render =
    children &&
    log.assert(
      typeof children === 'function',
      'QuickSearch children can only be a render function',
    )
      ? children
      : renderProps => {
          const input = (
            <QuickSearchInput
              {...renderProps}
              placeholder={placeholder}
              size="md"
            />
          )
          return button ? (
            <FlexGrid gutter="0px">
              <FlexGrid.Col>{input}</FlexGrid.Col>
              <FlexGrid.Col col="auto">
                <QuickSearchButton {...renderProps} size="md" />
              </FlexGrid.Col>
            </FlexGrid>
          ) : (
            input
          )
        }

  return (
    <QuickSearchForm {...state} {...props} ref={ref}>
      {render(state)}
    </QuickSearchForm>
  )
})

export const QuickSearch = Object.assign(_QuickSearch, {
  Form: QuickSearchForm,
  Input: QuickSearchInput,
  Button: QuickSearchButton,
})
